import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./styles/DisputeResolution.css";
import useTicketId from "../hooks/useTicketId";
import api, { API_ENDPOINTS, PARAMS } from "../api/axiosConfig";
import CryptoJS from 'crypto-js';

const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || 'lutdocs-demo-key';

function decryptTicketId(token) {
  try {
    const bytes = CryptoJS.AES.decrypt(decodeURIComponent(token), ENCRYPTION_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}

export default function DisputeResolution() {
  // Always call hooks unconditionally
  const location = useLocation();
  const ticketIdFromHook = useTicketId();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  const [ticketId, setTicketId] = useState(token ? null : ticketIdFromHook);

  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [status, setStatus] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Validate one-time token on mount and fetch ticketId
    if (token) {
      api.get(API_ENDPOINTS.TOKEN_VALIDATE, { params: { token } })
        .then(res => {
          if (res.data && res.data.status === 'VALID') {
            setTicketId(res.data.ticketId);
          }
        });
    }
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);
    setStatus(null);
    try {
      // Call backend API to close and pay the ticket
      console.log("Accepting dispute for ticket:", ticketId);
      console.log("API endpoint:", API_ENDPOINTS.CLOSE_PAY);
      const response = await api.patch(
        API_ENDPOINTS.CLOSE_PAY,
        null,
        { params: { [PARAMS.TICKET_ID]: ticketId } }
      );
      // The backend returns ResponseEntity<Boolean>, so response.data is the boolean
      if (response.data) {
        setStatus({ type: "success", text: "Dispute accepted successfully!" });
        setAccepted(true);
        // Redirect to Merchant Consent Thank You page after short delay
        setTimeout(() => {
          navigate(`/merchant-consent-thankyou?${PARAMS.TICKET_ID}=${ticketId}`);
        }, 1200);
      } else {
        setStatus({ type: "error", text: "Failed to accept. Try again." });
      }
    } catch {
      setStatus({ type: "error", text: "Failed to accept. Try again." });
    } finally {
      setAccepting(false);
    }
  };

  const handleReject = () => {
    navigate(`/upload?${PARAMS.TICKET_ID}=${ticketId}`);
  };

  return (
    <div className="container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div className="card" style={{ width: '100%', maxWidth: 600 }}>
        <div className="header">
          <h1>Dispute Resolution</h1>
          <div className="ticket-id-dispute">Ticket ID #{ticketId || '...'}</div>
        </div>

        <div className="content">
          <div className="intro">
            <h2>Resolve This Dispute</h2>
            <p>Please review and choose an action below</p>
          </div>

          <div className="action-buttons">
            <button
              onClick={handleAccept}
              disabled={accepting || accepted}
              className="btn btn-accept"
            >
              {accepting ? (
                <>
                  Processing <span className="loading"></span>
                </>
              ) : accepted ? (
                "Accepted ✓"
              ) : (
                "Accept Dispute"
              )}
            </button>
            {!accepted && (
              <button onClick={handleReject} className="btn btn-reject">
                Contest Dispute
              </button>
            )}
          </div>

          {status && (
            <div className={`status-message ${status.type}`}>
              {status.text}
            </div>
          )}
        </div>
      </div>
      <footer style={{ width: '100%', textAlign: 'center', marginTop: '2rem' }}>
        <p>HubSpot Dispute Resolution Portal &copy; 2025</p>
      </footer>
    </div>
  );
}
